import { useEffect, useState } from 'react';

interface ChangePasswordModalProps {
  isOpen: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (payload: {
    oldPassword: string;
    newPassword: string;
  }) => void | Promise<void>;
}

function validatePassword(value: string, label: string): string {
  if (!value) return `请输入${label}`;
  if (value.length < 6) return `${label}长度不能少于6位`;
  if (value.length > 36) return `${label}长度不能超过36位`;
  return '';
}

export default function ChangePasswordModal({
  isOpen,
  submitting = false,
  onCancel,
  onConfirm,
}: ChangePasswordModalProps) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    if (!isOpen) return;
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const nextErrors: typeof errors = {};
    const oldErr = validatePassword(oldPassword, '旧密码');
    const newErr = validatePassword(newPassword, '新密码');
    if (oldErr) nextErrors.oldPassword = oldErr;
    if (newErr) nextErrors.newPassword = newErr;
    if (!confirmPassword) {
      nextErrors.confirmPassword = '请输入确认密码';
    } else if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = '新密码和确认密码不一致';
    } else if (oldPassword && newPassword && oldPassword === newPassword) {
      nextErrors.newPassword = '新密码不能与旧密码相同';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    void onConfirm({ oldPassword, newPassword });
  };

  const fieldClass = (hasError?: string) =>
    `w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors disabled:bg-background-100 ${
      hasError
        ? 'border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-1 focus:ring-red-100'
        : 'border-background-300 focus:border-primary-400'
    }`;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40">
      <div className="bg-background-50 rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-primary-100">
            <i className="ri-lock-password-line text-primary-600 text-lg" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">修改密码</h3>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">
              旧密码 <span className="text-primary-500">*</span>
            </label>
            <input
              type="password"
              autoComplete="current-password"
              disabled={submitting}
              value={oldPassword}
              maxLength={36}
              placeholder="请输入旧密码"
              onChange={(e) => {
                setOldPassword(e.target.value);
                if (errors.oldPassword) setErrors((prev) => ({ ...prev, oldPassword: undefined }));
              }}
              className={fieldClass(errors.oldPassword)}
            />
            {errors.oldPassword ? (
              <p className="text-xs text-red-500 mt-1.5">{errors.oldPassword}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">
              新密码 <span className="text-primary-500">*</span>
            </label>
            <input
              type="password"
              autoComplete="new-password"
              disabled={submitting}
              value={newPassword}
              maxLength={36}
              placeholder="请输入新密码（6-36位）"
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: undefined }));
              }}
              className={fieldClass(errors.newPassword)}
            />
            {errors.newPassword ? (
              <p className="text-xs text-red-500 mt-1.5">{errors.newPassword}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">
              确认密码 <span className="text-primary-500">*</span>
            </label>
            <input
              type="password"
              autoComplete="new-password"
              disabled={submitting}
              value={confirmPassword}
              maxLength={36}
              placeholder="请再次输入新密码"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                }
              }}
              className={fieldClass(errors.confirmPassword)}
            />
            {errors.confirmPassword ? (
              <p className="text-xs text-red-500 mt-1.5">{errors.confirmPassword}</p>
            ) : null}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {submitting && <i className="ri-loader-4-line animate-spin text-sm" />}
            {submitting ? '提交中...' : '确认修改'}
          </button>
        </div>
      </div>
    </div>
  );
}
